import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { useMainCategories } from 'features/main-category/hooks/useMainCategories';
import AddServiceCatalogModal from 'features/service-catalog/components/AddServiceCatalogModal';
import DeleteConfirmModal from 'features/service-catalog/components/DeleteConfirmModal';
import EditServiceCatalogModal from 'features/service-catalog/components/EditServiceCatalogModal';
import ViewServiceCatalogModal from 'features/service-catalog/components/ViewServiceCatalogModal';
import { usePrefetchServiceCatalog } from 'features/service-catalog/hooks/usePrefetchServiceCatalog';
import { useServiceCatalog } from 'features/service-catalog/hooks/useServiceCatalog';
import { useToggleServiceCatalogStatus } from 'features/service-catalog/hooks/useToggleServiceCatalogStatus';
import { useURLSync } from 'features/service-catalog/hooks/useURLSync';
import { useSubCategories } from 'features/sub-category/hooks/useSubCategories';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  Plus,
  Search,
  Trash2,
  X,
  ClipboardList,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDateTime } from 'utils/dateFormatter';

const ServiceCatalog = () => {
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    mainCategory: 'all',
    subCategory: 'all',
    status: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const { data, isPending, isError, error } = useServiceCatalog(filters);
  const { mutate: toggleStatus } = useToggleServiceCatalogStatus();
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchServiceCatalog();
  const { data: mainCategories } = useMainCategories({ limit: 99 });

  const { data: subCategoryResponse } = useSubCategories({
    parentCategory: filters.mainCategory,
    limit: 100,
  });
  const subCategories = subCategoryResponse?.data || [];

  const { data: profileData } = useUserProfile();
  const user = profileData?.data;

  const restrictedRoles = [
    'delivery_manager',
    'travel_manager',
    'operations_manager',
    'finance_manager',
  ];

  const showPermissionError = restrictedRoles.includes(user?.role?.name);

  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'mainCategory' ? { subCategory: 'all' } : {}),
      page: key === 'page' ? value : '1',
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
    }));
  };

  const handleToggleStatus = (id) => {
    toggleStatus({ id });
  };

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column) return <span className='text-muted-foreground'>↕</span>;
    return filters.sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (entry) => {
    setIsEditModalOpen(true);
    setSelectedEntry(entry);
  };

  const handleDeleteClick = (entry) => {
    setSelectedEntry(entry);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (entry) => {
    setSelectedEntry(entry);
    setIsViewModalOpen(true);
  };

  return (
    <Layout>
      <div className={`min-h-screen bg-background text-foreground p-6 ${PAGE_CONTENT_WIDTH}`}>
          {/* Header */}
          <PageHeader
            icon={ClipboardList}
            title="Service Catalog Management"
            subtitle="Curate catalog entries that providers can list against"
            actions={
              !showPermissionError && (
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className='flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
                >
                  <Plus size={16} />
                  <span>Add Catalog Entry</span>
                </button>
              )
            }
          />

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6'>
            {/* Search */}
            <div className='relative lg:col-span-2'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground' size={16} />
              <input
                type='text'
                placeholder='Search by name...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Main Category Filter */}
            <select
              value={filters.mainCategory}
              onChange={(e) => updateFilter('mainCategory', e.target.value)}
              className='px-2 py-2 text-sm'
            >
              <option value='all'>All Main Categories</option>
              {mainCategories?.data?.map((parent) => (
                <option key={parent._id} value={parent._id}>
                  {parent.name?.english}
                </option>
              ))}
            </select>

            {/* Sub Category Filter */}
            <select
              value={filters.subCategory}
              onChange={(e) => updateFilter('subCategory', e.target.value)}
              disabled={filters.mainCategory === 'all'}
              className='px-2 py-2 text-sm disabled:opacity-50'
            >
              <option value='all'>All Sub Categories</option>
              {subCategories.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name?.english}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Status</option>
              <option value='active'>Active</option>
              <option value='inactive'>Inactive</option>
            </select>
          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden elevation-1'>
            <div className='overflow-x-auto custom-scrollbar'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Image</th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('name')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Name</span>
                        <SortIcon column='name' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Main Category</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>Sub Category</th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('status')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Status</span>
                        <SortIcon column='status' />
                      </div>
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Created</span>
                        <SortIcon column='createdAt' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>Actions</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {showPermissionError ? (
                    <tr>
                      <td colSpan='7' className='px-4 py-8 text-center text-destructive'>
                        Error loading Service Catalog: You do not have permission to perform this action!
                      </td>
                    </tr>
                  ) : isPending ? (
                    <tr>
                      <td colSpan={7} className='px-4 py-8 text-center text-muted-foreground'>
                        Loading catalog entries...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td colSpan={7} className='px-4 py-8 text-center text-destructive'>
                        Error loading catalog entries: {error?.message || 'Something went wrong'}
                      </td>
                    </tr>
                  ) : data?.data?.length === 0 ? (
                    <tr>
                      <td colSpan={7} className='px-4 py-8 text-center text-muted-foreground'>
                        No catalog entries found
                      </td>
                    </tr>
                  ) : (
                    data?.data?.map((entry) => (
                      <tr key={entry._id} className='hover:bg-accent/50 transition-colors'>
                        {/* Image Column */}
                        <td className='px-4 py-3'>
                          {entry.image ? (
                            <img
                              src={entry.image}
                              alt={entry.name}
                              className='w-10 h-10 rounded-lg object-cover border border-border'
                            />
                          ) : (
                            <div className='w-10 h-10 rounded-lg bg-muted flex items-center justify-center'>
                              <span className='text-xs text-muted-foreground'>No Image</span>
                            </div>
                          )}
                        </td>

                        {/* Name Column */}
                        <td className='px-4 py-3 text-sm font-medium whitespace-nowrap'>
                          <div className='text-foreground'>{entry.name}</div>
                        </td>

                        {/* Main Category Column */}
                        <td className='px-4 py-3 text-sm'>
                          {entry.mainCategory ? (
                            <span className='px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium whitespace-nowrap'>
                              {entry.mainCategory.name?.english}
                            </span>
                          ) : (
                            <span className='text-muted-foreground text-xs'>N/A</span>
                          )}
                        </td>

                        {/* Sub Category Column */}
                        <td className='px-4 py-3 text-sm'>
                          {entry.subCategory ? (
                            <span className='px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-md text-xs font-medium whitespace-nowrap'>
                              {entry.subCategory.name?.english}
                            </span>
                          ) : (
                            <span className='text-muted-foreground text-xs'>N/A</span>
                          )}
                        </td>

                        {/* Status Column - SWITCH */}
                        <td className='px-4 py-3 text-sm'>
                          <label className='relative inline-flex items-center cursor-pointer group'>
                            <input
                              type='checkbox'
                              checked={entry.status === 'active'}
                              onChange={() => handleToggleStatus(entry._id)}
                              className='sr-only peer'
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                            <span className='ms-3 text-sm font-medium text-foreground group-hover:text-primary transition-colors'>
                              {entry.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </td>

                        {/* Created Date Column */}
                        <td className='px-4 py-3 text-sm text-muted-foreground'>{formatDateTime(entry.createdAt)}</td>

                        {/* Actions Column */}
                        <td className='px-4 py-3 text-sm text-right'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='View Details'
                              onClick={() => handleViewClick(entry)}
                            >
                              <Eye size={16} className='text-muted-foreground hover:text-foreground' />
                            </button>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit Catalog Entry'
                              onClick={() => handleEditClick(entry)}
                            >
                              <Edit size={16} className='text-muted-foreground hover:text-foreground' />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(entry)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Delete Catalog Entry'
                            >
                              <Trash2 size={16} className='text-muted-foreground hover:text-destructive' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!showPermissionError && data?.pagination && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing {(data.pagination.currentPage - 1) * data.pagination.itemsPerPage + 1} to{' '}
                  {Math.min(data.pagination.currentPage * data.pagination.itemsPerPage, data.pagination.totalItems)} of{' '}
                  {data.pagination.totalItems} catalog entries
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() => updateFilter('page', String(data.pagination.currentPage - 1))}
                    disabled={data.pagination.currentPage === 1}
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(data.pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = data.pagination.currentPage;

                      if (page === 1 || page === data.pagination.totalPages || (page >= current - 1 && page <= current + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card hover:bg-accent border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className='text-muted-foreground'>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => updateFilter('page', String(data.pagination.currentPage + 1))}
                    disabled={data.pagination.currentPage === data.pagination.totalPages}
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Modals */}
        <AddServiceCatalogModal isAddModalOpen={isAddModalOpen} setIsAddModalOpen={setIsAddModalOpen} />
        <EditServiceCatalogModal isEditModalOpen={isEditModalOpen} setIsEditModalOpen={setIsEditModalOpen} entry={selectedEntry} />
        <DeleteConfirmModal isDeleteModalOpen={isDeleteModalOpen} setIsDeleteModalOpen={setIsDeleteModalOpen} entry={selectedEntry} />
        <ViewServiceCatalogModal isViewModalOpen={isViewModalOpen} setIsViewModalOpen={setIsViewModalOpen} entry={selectedEntry} />
      </div>
    </Layout>
  );
};

export default ServiceCatalog;
